import { identify, trackPageView } from '@app/analytics/use-analytics';
import { FLAG_SPACES } from '@app/feature-flags/flags';
import {
  MyProfileDocument,
  MyProfileQuery,
  MyProfileQueryVariables,
  OrganizationDetailsDocument,
  OrganizationDetailsQuery,
  OrganizationDetailsQueryVariables,
  OrganizationsDocument,
  OrganizationSpaceExistsDocument,
  OrganizationSpaceExistsQuery,
  OrganizationSpaceExistsQueryVariables,
  OrganizationSpacesDocument,
  OrganizationSpacesQuery,
  OrganizationSpacesQueryVariables,
  OrganizationsQuery,
  OrganizationsQueryVariables,
  VendorBoothsDocument,
  VendorBoothsQuery,
  VendorBoothsQueryVariables,
} from '@app/graphql/types-and-hooks';
import { ORG_SPACES_PAGE_SIZE } from '@app/routes/organization/constants';
import { OrgSpace, PageInfo } from '@app/routes/organization/domain';
import {
  selectCurrentSpace,
  selectOrgSpaces,
  selectOrgSpacesFeatureFlagEnabled,
  selectOrgSpacesPageInfo,
  selectOrgSpacesStatus,
} from '@app/routes/organization/selectors';
import { updateLastVisitedSpace } from '@app/spaces/storage-spaces-cache';
import {
  authenticateScoped,
  fetchContentApi,
  gqlQuery,
} from '@app/store/fetch-saga';
import {
  getOrganizationId,
  getUserExternalId,
  selectFlags,
  selectOrganizationsPageInfo,
  selectUserAuthToken,
  selectVendorBoothsPageInfo,
} from '@app/store/selectors';
import {
  all,
  call,
  fork,
  NarrowStoreEvent,
  put,
  SagaGenerator,
  select,
  take,
  takeLatest,
  takeLeading,
} from '@app/store/typed-saga-effects';
import { logger } from '@app/utilities/logger';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { getOrganizationIdFromPath } from '@hopin-team/dashboard-shared';
import { matchRoutes } from 'react-router-dom';
import { CallEffect } from 'redux-saga/effects';

import { routes } from '../routes';
import { deleteEdit } from '../routes/content/routes/recordings/delete-edit';
import { State } from './state';

export function* rootEffect() {
  yield* takeLatest('router - route changed', handleRouteChangedEvent);
  yield* takeLeading('[advanced-editor] delete edit requested', deleteEdit);

  yield* takeLatest('reactive-map - organization changed', handleOrgChange);
  yield* takeLatest('reactive-map - user updated', handleUserUpdatedForDatadog);
  yield* takeLatest('[organization] org space changed', persistSpaceSelection);
  yield* takeLatest(
    '[organization] initial spaceid resolved',
    persistSpaceSelection,
  );
  yield* takeLatest('[nav-bar] organizations fetch', fetchOrganizations);
  yield* takeLatest('[nav-bar] vendor-booths fetch', fetchVendorBooths);
  yield* takeLatest(
    'reactive-map - feature flags updated',
    fetchOrgSpacesWhenSpacesFlagEnabled,
  );
  yield* takeLatest(
    '[organization] fetch org spaces',
    function* ({
      payload,
    }: NarrowStoreEvent<'[organization] fetch org spaces'>) {
      yield* call(fetchOrgSpaces, {
        loadMore: payload?.loadMore,
      });
    },
  );
  yield* takeLatest(
    '[organization] validate current space access',
    validateCurrentSpaceAccess,
  );
  yield* takeLatest('download-url - fetch requested', handleFetchDownloadUrl);

  yield* call(initialiseOrganizationId);
  const authToken = yield* select(selectUserAuthToken);
  if (!authToken) {
    yield* takeLatest(
      'reactive-map - auth token updated',
      function* ({
        payload,
      }: NarrowStoreEvent<'reactive-map - auth token updated'>) {
        if (!payload.authToken) return;
        yield* call(fetchUserProfile);
      },
    );
  } else {
    yield* fork(fetchUserProfile);
  }
}

const routeEffects: Record<
  string,
  undefined | (() => Promise<{ default: any }>)
> = {
  '/org/new': () => import('../routes/create-organization/effect'),
  '/attendee': () => import('../routes/attendee/effect'),
  '/connections/*': () => import('../routes/connections/effect'),
  '/org/:org_id/content': () => import('../routes/content/effect'),
  hubs: () => import('../routes/content/routes/pages/effect'),
  '/org/:org_id/analytics': () => import('../features/analytics/effect'),
  '/org/:org_id/analytics-iframe': () => import('../features/analytics/effect'),
  library: () => import('../routes/content/routes/library/effect'),
  'recordings/view/:recordingId': () =>
    import('../routes/content/routes/recordings/view/effect'),
  '/org/:org_id/apps': () => import('../routes/apps/effect'),
  '/org/:org_id/manage': () =>
    import('../routes/organization/routes/manage/effect'),
  '/org/:org_id': () => import('../routes/organization/effect'),
  events: () => import('../routes/organization/routes/events/effect'),
  series: () => import('../routes/organization/routes/series/effect'),
  '/org/:org_id/create-event-wizard/*': () =>
    import('../routes/create-event-wizard/effect'),
  '/create-event-wizard/*': () =>
    import('../routes/create-event-wizard/effect'),
  '/org/:org_id/content/recordings/:recordingId': () =>
    import('../routes/content/routes/recordings/effect'),
  lab: () => import('../routes/content/routes/recordings/routes/lab/effect'),
  profile: () => import('../routes/attendee/routes/profile/effect'),
  billing: () =>
    import('../routes/organization/routes/manage/routes/billing/effect'),
  spaces: () =>
    import('../routes/organization/routes/manage/routes/spaces/effect'),
  team: () => import('../routes/organization/routes/manage/routes/team/effect'),
  'active-members': () =>
    import(
      '../routes/organization/routes/manage/routes/team/routes/active-member/effect'
    ),
  'pending-invites': () =>
    import(
      '../routes/organization/routes/manage/routes/team/routes/pending-invites/effect'
    ),
  'team-access': () =>
    import('../routes/organization/routes/manage-event/effect'),
};

/**
 * When the route changes, look up the pathname against the routes table.
 * If there is route, look up routeEffects table to find a matching effect.
 * If there are effects, call them.
 */
function* handleRouteChangedEvent({
  payload: { pathname },
}: NarrowStoreEvent<'router - route changed'>) {
  const results = matchRoutes(routes, pathname);

  const organizationId = yield* call(
    getOrganizationIdFromPath,
    window.location.pathname,
  );
  // note: for attendee and vendor pages, the organizationId will be undefined
  yield* fork(trackPageView, { organizationId });

  if (results === null) return;

  const effects = results.reduce<SagaGenerator<void, CallEffect<void>>[]>(
    (effects, result) => {
      if (!result.route.path) return effects;

      const effect = routeEffects[result.route.path];

      if (effect === undefined) return effects;

      effects.push(
        call(function* () {
          const module: {
            default: () => SagaGenerator<void, CallEffect<void>>;
          } = yield* call(effect);
          yield* call(module.default);
        }),
      );

      return effects;
    },
    [],
  );

  yield all(effects);
}

/** When initialising the app, add the organization id to the store. */
function* initialiseOrganizationId() {
  const organizationExternalId = yield* call(
    getOrganizationIdFromPath,
    window.location.pathname,
  );

  if (!organizationExternalId) return;
  yield* put({
    type: 'reactive-map - organization changed',
    payload: { organizationExternalId },
  });
  yield* put({
    type: '[nav-bar] organization selected',
    payload: { isOrgSelected: true },
  });
}

function* handleOrgChange() {
  const authToken = yield* select(selectUserAuthToken);

  if (!authToken) {
    yield* takeLatest(
      'reactive-map - auth token updated',
      function* ({
        payload,
      }: NarrowStoreEvent<'reactive-map - auth token updated'>) {
        if (!payload.authToken) return;
        yield* call(fetchOrganizationDetails);
        yield* call(fetchOrgSpaces);
      },
    );
  } else {
    yield* fork(fetchOrganizationDetails);
    yield* fork(fetchOrgSpaces);
  }
}

function* handleFetchDownloadUrl({
  payload: { filename },
}: NarrowStoreEvent<'download-url - fetch requested'>) {
  try {
    yield* put({ type: 'download-url - fetch started', payload: { filename } });

    const organizationExternalId = yield* select(getOrganizationId);
    if (!organizationExternalId) {
      throw new Error('Organization ID not found');
    }

    const contentHubAuth = yield* select(
      (state: State) => state.contentHub.authentication,
    );

    if (contentHubAuth.status !== 'success') {
      const authResult = yield* call(authenticateScoped, {
        scope: 'content_api',
        organization_external_id: organizationExternalId,
      });

      if (!authResult) {
        throw new Error('Content hub authentication failed');
      }

      yield* put({
        type: '[content] authentication success',
        payload: authResult,
      });
    }

    const response = yield* call(() =>
      fetchContentApi<{ filename: string; url: string; expiresIn: number }>(
        `/${organizationExternalId}/files/download-url?filename=${encodeURIComponent(
          filename,
        )}`,
      ),
    );

    if (!response?.url) {
      throw new Error('Failed to fetch download url');
    }

    yield* put({
      type: 'download-url - fetch success',
      payload: {
        filename,
        url: response.url,
        expiresAt: Date.now() + response.expiresIn * 1000,
      },
    });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch download url';
    yield* put({
      type: 'download-url - fetch failed',
      payload: { filename, error: errorMessage },
    });
    logger.error(`Failed to fetch download url for ${filename}`, error);
  }
}

export function* persistSpaceSelection({
  payload: { spaceExternalId },
}: {
  payload: { spaceExternalId: string };
}) {
  const userId = yield* select(getUserExternalId);
  const organizationId = yield* select(getOrganizationId);
  if (!userId || !organizationId || !spaceExternalId) return;

  yield* call(updateLastVisitedSpace, userId, organizationId, spaceExternalId);
}

function* fetchOrganizationDetails() {
  try {
    yield* put({ type: '[organization] fetch organization details started' });

    const organizationId = yield* select(getOrganizationId);

    const result = yield* call(() =>
      gqlQuery<OrganizationDetailsQuery, OrganizationDetailsQueryVariables>(
        OrganizationDetailsDocument,
        {
          organizationId: organizationId as string,
        },
      ),
    );

    if (!result || result.error) {
      const errorMessage = result?.error?.message || '';
      yield* put({
        type: '[organization] fetch organization details failed',
        payload: {
          error: {
            message: errorMessage || 'Failed to fetch organization details',
          },
        },
      });
      logger.error(
        `Failed to fetch organization details`,
        errorMessage ? { errorMessage } : undefined,
      );
      return;
    }

    if (!result.data?.organization) {
      yield* put({
        type: '[organization] fetch organization details failed',
        payload: {
          error: { message: 'No organization data found' },
        },
      });
      logger.error(`Failed to fetch organization details`, {
        errorMessage: 'No organization data found',
      });
      return;
    }

    const {
      name,
      about,
      email,
      facebookUrl,
      linkedinUrl,
      twitterUrl,
      websiteUrl,
    } = result.data?.organization ?? {};

    const organization = result.data?.organization || {};
    let standaloneStudio = null;
    if (organization.standaloneStudios?.length > 0) {
      standaloneStudio = {
        name: organization.standaloneStudios[0].name || '',
        uuid: organization.standaloneStudios[0].uuid || '',
        eventSlug: organization.standaloneStudios[0].event?.slug || '',
      };
    }

    yield* put({
      type: '[organization] fetch organization details success',
      payload: {
        name: name || '',
        about: about || '',
        email: email || '',
        facebookUrl: facebookUrl || null,
        linkedinUrl: linkedinUrl || null,
        twitterUrl: twitterUrl || null,
        websiteUrl: websiteUrl || null,
        features: {
          eventsLite: result.data?.organization?.features?.eventsLite || false,
          eventsPro: result.data?.organization?.features?.eventsPro || false,
          onsiteHybridEvents:
            result.data?.organization?.features?.onsiteHybridEvents || false,
          appStore: result.data?.organization?.features?.appStore || false,
        },
        paymentPlan: {
          planName: result.data?.organization?.paymentPlan?.planName || '',
          downgradedToFree:
            result.data?.organization?.paymentPlan?.downgradedToFree || false,
          endsAt: result.data?.organization?.paymentPlan?.endsAt || '',
          planNameLocale:
            result.data?.organization?.paymentPlan?.planNameLocale || '',
          isAdvanced:
            result.data?.organization?.paymentPlan?.isAdvanced || false,
        },
        image: result.data?.organization?.image?.medium || '',
        smallImage: result.data?.organization?.image?.small || '',
        largeImage: result.data?.organization?.image?.large || '',
        createdAt: result.data?.organization?.createdAt || '',
        standaloneStudio,
        settings: {
          disableAiFeatures:
            result.data?.organization?.settings?.disableAiFeatures || false,
        },
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '';
    yield* put({
      type: '[organization] fetch organization details failed',
      payload: {
        error: {
          message: errorMessage || 'Failed to fetch organization details',
        },
      },
    });
    logger.error(`Failed to fetch organization details`, error);
  }
}

export function* fetchOrgSpacesWhenSpacesFlagEnabled({
  payload: { featureFlags },
}: NarrowStoreEvent<'reactive-map - feature flags updated'>) {
  const wasEnabled = yield* select(selectOrgSpacesFeatureFlagEnabled);
  const isEnabled = Boolean(featureFlags?.[FLAG_SPACES]);

  if (!isEnabled || wasEnabled) return;

  yield* put({ type: '[org-spaces] flag enabled' });
  yield* call(fetchOrgSpaces);
}

function* isStaleValidation(organizationId: string, spaceExternalId: string) {
  const currentOrganizationId = yield* select(getOrganizationId);
  const currentSpace = yield* select(selectCurrentSpace);

  return (
    currentOrganizationId !== organizationId ||
    currentSpace.id !== spaceExternalId
  );
}

export function* validateCurrentSpaceAccess() {
  const authToken = yield* select(selectUserAuthToken);
  const flags = yield* select(selectFlags);
  const organizationId = yield* select(getOrganizationId);
  const currentSpace = yield* select(selectCurrentSpace);

  if (!authToken || !flags?.[FLAG_SPACES] || !organizationId) return;
  if (!currentSpace.id) return;

  try {
    const result = yield* call(() =>
      gqlQuery<
        OrganizationSpaceExistsQuery,
        OrganizationSpaceExistsQueryVariables
      >(
        OrganizationSpaceExistsDocument,
        {
          organizationId,
          spaceExternalId: currentSpace.id,
        },
        { requestPolicy: 'network-only' },
      ),
    );

    // The org or the space may have changed while this request was in flight;
    // a result for a space that is no longer selected must not switch the user
    // away from their newer selection.
    if (yield* call(isStaleValidation, organizationId, currentSpace.id)) return;

    if (result?.error) {
      logger.error(`Failed to validate current space access`, {
        errorMessage: result.error.message,
      });
      return;
    }

    const edges = result?.data?.organization?.spaces?.edges ?? [];
    const isValid = edges.some(edge => Boolean(edge?.node));
    if (isValid) return;

    // if a fetch is already running,
    // wait for it before reading the fallback space.
    const orgSpacesStatus = yield* select(selectOrgSpacesStatus);
    if (orgSpacesStatus === 'processing') {
      yield* take([
        '[organization] fetch org spaces success',
        '[organization] fetch org spaces failed',
      ]);

      if (yield* call(isStaleValidation, organizationId, currentSpace.id))
        return;
    }

    const spaces = yield* select(selectOrgSpaces);
    const firstSpace = spaces[0];

    if (!firstSpace || !firstSpace.externalId) return;

    const previousSpaceName = currentSpace?.name ?? '';

    yield* put({
      type: '[organization] org space changed',
      payload: {
        spaceExternalId: firstSpace?.externalId ?? '',
        spaceName: firstSpace?.name ?? '',
      },
    });
    yield* put({
      type: '[organization] invalid space access modal open',
      payload: {
        previousSpaceName,
        newSpaceName: firstSpace?.name ?? '',
      },
    });
  } catch (error) {
    const currentOrganizationId = yield* select(getOrganizationId);
    if (currentOrganizationId !== organizationId) return;

    logger.error(`Failed to validate current space access`, error);
  }
}

export function* fetchOrgSpaces({
  loadMore = false,
}: { loadMore?: boolean } = {}) {
  const authToken = yield* select(selectUserAuthToken);
  const flags = yield* select(selectFlags);
  const organizationId = yield* select(getOrganizationId);
  const pageInfo = yield* select(selectOrgSpacesPageInfo);

  if (!authToken || !flags?.[FLAG_SPACES] || !organizationId) return;

  if (loadMore && !pageInfo.hasNextPage) {
    return;
  }

  const after = loadMore ? pageInfo.endCursor : null;

  try {
    yield* put({ type: '[organization] fetch org spaces started' });

    const result = yield* call(() =>
      gqlQuery<OrganizationSpacesQuery, OrganizationSpacesQueryVariables>(
        OrganizationSpacesDocument,
        { organizationId, first: ORG_SPACES_PAGE_SIZE, after },
        { requestPolicy: 'network-only' },
      ),
    );

    // The user may have switched orgs while this request was in flight —
    // that switch already reset `orgSpaces` to its default state. Bail out
    // rather than dispatching this now-stale org's result over the new
    // org's central spaces state.
    const currentOrganizationId = yield* select(getOrganizationId);
    if (currentOrganizationId !== organizationId) return;

    if (!result || result.error) {
      const errorMessage = result?.error?.message || '';
      yield* put({ type: '[organization] fetch org spaces failed' });
      logger.error(
        `Failed to fetch org spaces`,
        errorMessage ? { errorMessage } : undefined,
      );
      return;
    }

    const connection = result.data?.organization?.spaces;

    if (!connection) {
      yield* put({ type: '[organization] fetch org spaces failed' });
      logger.error(`Failed to fetch org spaces`, {
        errorMessage: 'Response contained no spaces connection',
      });
      return;
    }

    const spaces: OrgSpace[] = (connection.edges ?? []).flatMap(edge =>
      edge.node
        ? [{ externalId: edge.node.externalId, name: edge.node.name }]
        : [],
    );

    const nextPageInfo: PageInfo = {
      hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
      endCursor: connection?.pageInfo?.endCursor ?? null,
    };

    yield* put({
      type: '[organization] fetch org spaces success',
      payload: { spaces, pageInfo: nextPageInfo, append: loadMore },
    });
  } catch (error) {
    // Same stale-org guard as above: an old org's request that throws
    // after the org has already switched must not mark the new org's
    // spaces fetch as failed.
    const currentOrganizationId = yield* select(getOrganizationId);
    if (currentOrganizationId !== organizationId) return;

    yield* put({ type: '[organization] fetch org spaces failed' });
    logger.error(`Failed to fetch org spaces`, error);
  }
}

function* handleUserUpdatedForDatadog({
  payload: { id: externalId },
}: NarrowStoreEvent<'reactive-map - user updated'>) {
  if (!externalId) return;

  if (process.env.NODE_ENV !== 'development') {
    yield* call([datadogRum, 'setUser'], {
      id: externalId,
    });
    yield* call([datadogLogs, 'setUser'], {
      id: externalId,
    });
  }
}

function* fetchUserProfile() {
  try {
    yield* put({ type: '[nav-bar] user-profile fetch started' });

    const result = yield* call(() =>
      gqlQuery<MyProfileQuery, MyProfileQueryVariables>(MyProfileDocument, {}),
    );

    if (!result || result.error) {
      const errorMessage = result?.error?.message || '';
      yield* put({
        type: '[nav-bar] user-profile fetch failed',
        payload: {
          error: errorMessage || 'Failed to fetch user profile',
        },
      });
      logger.error(
        `Failed to fetch user profile`,
        errorMessage
          ? {
              errorMessage,
            }
          : undefined,
      );
      return;
    }

    const userData = result.data?.me;
    if (!userData) {
      yield* put({
        type: '[nav-bar] user-profile fetch failed',
        payload: {
          error: 'No user data found',
        },
      });
      logger.error(`Failed to fetch user profile`, {
        errorMessage: 'No user data found',
      });
      return;
    }

    yield* put({
      type: '[nav-bar] user-profile fetch success',
      payload: {
        data: {
          id: userData.id,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          avatarImage: userData.image.small || '',
          largeAvatarImage: userData.image.large || '',
          hasAcceptedTerms: userData.hasAcceptedTerms || false,
        },
      },
    });
    yield* fork(identify, userData.id);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '';
    yield* put({
      type: '[nav-bar] user-profile fetch failed',
      payload: {
        error: errorMessage || 'Failed to fetch user profile',
      },
    });
    logger.error(`Failed to fetch user profile`, error);
  }
}

function* fetchOrganizations() {
  const authToken = yield* select(selectUserAuthToken);
  if (!authToken) return;

  yield* put({ type: '[nav-bar] organizations fetch started' });
  const currentPageInfo = yield* select(selectOrganizationsPageInfo);
  try {
    const result = yield* call(() =>
      gqlQuery<OrganizationsQuery, OrganizationsQueryVariables>(
        OrganizationsDocument,
        {
          pageSize: 10,
          cursor: currentPageInfo.endCursor || null,
        },
      ),
    );

    if (!result || result.error) {
      const errorMessage = result?.error?.message || '';
      yield* put({
        type: '[nav-bar] organizations fetch failed',
        payload: {
          error: errorMessage || 'Failed to fetch organizations',
        },
      });
      logger.error(`Failed to fetch organizations`, {
        errorMessage,
        isFirstPage: !currentPageInfo.endCursor,
      });
      return;
    }

    const organizations =
      result.data?.me?.organizations?.edges?.flatMap(edge =>
        edge?.node
          ? {
              id: edge.node.id,
              name: edge.node.name,
              plan: edge.node.paymentPlan?.planName || undefined,
              planNameLocale: edge.node.paymentPlan?.planNameLocale || '',
              image: edge.node.image.medium,
              smallImage: edge.node.image.small,
              largeImage: edge.node.image.large,
              createdAt: edge.node.createdAt,
              features: edge.node.features,
            }
          : [],
      ) ?? [];

    yield* put({
      type: '[nav-bar] organizations fetch success',
      payload: {
        data: organizations,
        pageInfo: {
          hasNextPage:
            result.data?.me?.organizations?.pageInfo?.hasNextPage || false,
          endCursor: result.data?.me?.organizations?.pageInfo?.endCursor || '',
        },
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '';
    yield* put({
      type: '[nav-bar] organizations fetch failed',
      payload: {
        error: errorMessage || 'Failed to fetch organizations',
      },
    });
    logger.error(`Failed to fetch organizations`, error);
  }
}

function* fetchVendorBooths() {
  const authToken = yield* select(selectUserAuthToken);
  if (!authToken) return;

  yield* put({ type: '[nav-bar] vendor-booths fetch started' });
  const currentPageInfo = yield* select(selectVendorBoothsPageInfo);
  try {
    const result = yield* call(() =>
      gqlQuery<VendorBoothsQuery, VendorBoothsQueryVariables>(
        VendorBoothsDocument,
        {
          pageSize: 10,
          cursor: currentPageInfo.endCursor || null,
        },
      ),
    );

    if (!result || result.error) {
      const errorMessage = result?.error?.message || '';
      yield* put({
        type: '[nav-bar] vendor-booths fetch failed',
        payload: {
          error: errorMessage || 'Failed to fetch vendor booths',
        },
      });
      logger.error(`Failed to fetch vendor booths`, {
        errorMessage,
        isFirstPage: !currentPageInfo.endCursor,
      });
      return;
    }

    const vendorBooths =
      result.data?.me?.vendorBooths?.edges?.flatMap(edge =>
        edge?.node
          ? {
              id: edge.node.id,
              name: edge.node.name,
              eventName: edge.node.event.name,
              image: edge.node.image.url,
            }
          : [],
      ) ?? [];

    yield* put({
      type: '[nav-bar] vendor-booths fetch success',
      payload: {
        data: vendorBooths,
        pageInfo: {
          hasNextPage:
            result.data?.me?.vendorBooths?.pageInfo?.hasNextPage || false,
          endCursor: result.data?.me?.vendorBooths?.pageInfo?.endCursor || '',
        },
      },
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : '';
    yield* put({
      type: '[nav-bar] vendor-booths fetch failed',
      payload: {
        error: errorMessage || 'Failed to fetch vendor booths',
      },
    });
    logger.error(`Failed to fetch vendor booths`, error);
  }
}
